import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { songsApi } from '@/services/api/songs';
import { usePlaylists } from '@/context/PlaylistContext';
import { useAudioPlayer } from '@/context/AudioPlayerContext';
import { Playlist } from '@/types/playlist';
import { Song, SongRevision } from '@/types/song';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import CoverArt from '@/components/common/CoverArt';
import { ArrowLeft, Plus, Play, Trash2, Music } from 'lucide-react';

export default function PlaylistDetailsPage() {
  const { playlistId } = useParams<{ playlistId: string }>();
  const { getPlaylist, removeTrackFromPlaylist } = usePlaylists();
  const { playSong, playQueue } = useAudioPlayer();
  const [playlist, setPlaylist] = useState<Playlist | null>(null);
  const [loading, setLoading] = useState(true);

  // Get unique song IDs from playlist
  const songIds = playlist?.songs.map(track => track.songId) || [];
  const uniqueSongIds = Array.from(new Set(songIds));
  const uniqueSongIdsKey = uniqueSongIds.join(','); // Stable string key for React Query

  // Fetch song details for all songs in the playlist
  const { data: songsData } = useQuery({
    queryKey: ['playlist-songs', uniqueSongIdsKey],
    queryFn: async () => {
      const songs = await Promise.all(
        uniqueSongIds.map(songId =>
          songsApi.getSongDetails(songId, { includeRevisions: true, includeComments: false })
        )
      );
      return songs;
    },
    enabled: uniqueSongIds.length > 0,
  });

  useEffect(() => {
    loadPlaylist();
  }, [playlistId]);

  async function loadPlaylist() {
    if (!playlistId) return;
    try {
      const data = await getPlaylist(playlistId);
      setPlaylist(data || null);
    } catch (error) {
      console.error('Failed to load playlist:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleRemoveTrack(trackIndex: number) {
    if (!playlistId) return;
    await removeTrackFromPlaylist(playlistId, trackIndex);
    await loadPlaylist();
  }

  if (loading) return <LoadingSpinner />;
  if (!playlist) {
    return <div className="text-center text-white p-8">Playlist not found</div>;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-surface border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <Link to="/playlists" className="flex items-center gap-2 text-text-secondary hover:text-white">
            <ArrowLeft size={20} />
            Back to Playlists
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Playlist Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">{playlist.name}</h1>
          {playlist.description && (
            <p className="text-text-secondary mb-4">{playlist.description}</p>
          )}
          <div className="flex items-center gap-4">
            {playlist.songs.length > 0 && songsData && (
              <button
                onClick={() => {
                  const queueItems = playlist.songs.map(track => {
                    const songData = songsData.find(s => s.song.songId === track.songId);
                    if (!songData) return null;

                    if (track.revisionId) {
                      const revision = songData.revisions.find(r => r.songRevisionId === track.revisionId);
                      return { song: songData.song, revision };
                    }
                    return { song: songData.song };
                  }).filter(Boolean) as Array<{ song: Song; revision?: SongRevision }>;

                  playQueue(queueItems);
                }}
                className="btn-primary flex items-center gap-2"
              >
                <Play size={18} />
                Play All
              </button>
            )}
            <Link
              to={`/playlists/${playlist.id}/add-tracks`}
              className="btn-secondary flex items-center gap-2"
            >
              <Plus size={18} />
              Add Tracks
            </Link>
          </div>
        </div>

        {/* Track List */}
        {playlist.songs.length === 0 ? (
          <div className="text-center text-text-secondary py-12">
            <Music size={48} className="mx-auto mb-4 opacity-50" />
            <p className="mb-4">No tracks in this playlist yet</p>
            <Link
              to={`/playlists/${playlist.id}/add-tracks`}
              className="btn-primary inline-flex items-center gap-2"
            >
              <Plus size={18} />
              Add Tracks
            </Link>
          </div>
        ) : (
          <div className="space-y-2">
            {playlist.songs.map((track, index) => (
              <div key={index} className="card flex items-center gap-4 group">
                {/* Clickable Track Area */}
                <div
                  onClick={() => {
                    const songData = songsData?.find(s => s.song.songId === track.songId);
                    if (!songData) return;

                    if (track.revisionId) {
                      const revision = songData.revisions.find(r => r.songRevisionId === track.revisionId);
                      if (revision) {
                        playSong(songData.song, revision);
                      }
                    } else {
                      playSong(songData.song);
                    }
                  }}
                  className="flex items-center gap-4 flex-1 min-w-0 cursor-pointer hover:opacity-80 transition-opacity"
                >
                  {/* Cover Art */}
                  <CoverArt
                    songId={track.songId}
                    revisionId={track.revisionId}
                    coverArtUrl={track.coverArtUrl}
                    alt={track.songName}
                    className="w-12 h-12 object-cover rounded"
                  />

                  {/* Track Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">
                      {track.songName}
                    </p>
                    {track.revisionName && (
                      <p className="text-text-secondary text-sm truncate">
                        {track.revisionName}
                      </p>
                    )}
                  </div>
                </div>

                {/* Delete Button */}
                <button
                  onClick={() => handleRemoveTrack(index)}
                  className="text-text-secondary hover:text-red-500 p-2"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
